import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, RefreshCw, AlertCircle, Hash, FileText } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function ParametrosGlobalesList() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { token } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

    const cargarDatos = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/parametrizacion/parametros-globales`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Error al cargar parámetros');
            const result = await response.json();
            setData(result);
            setError(null);
        } catch (error) {
            console.error('Error:', error);
            setError('No se pudieron cargar los parámetros globales.');
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarDatos();
    }, []);

    const handleDelete = async (clave) => {
        if (window.confirm(`¿Estás seguro de que deseas eliminar el parámetro "${clave}"?`)) {
            try {
                const response = await fetch(`${API_URL}/parametrizacion/parametros-globales/${clave}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    toast.success('Parámetro eliminado');
                    cargarDatos();
                } else {
                    throw new Error('Error al eliminar');
                }
            } catch (error) {
                toast.error('No se pudo eliminar el parámetro');
            }
        }
    };

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-enterprise">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-[#1e293b] p-3 flex justify-between items-center text-white rounded-t-sm shadow-md">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion/global')}
                        className="p-1 hover:bg-slate-700 rounded transition-colors text-slate-400 hover:text-white"
                        title="Volver"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-sm font-bold tracking-tight uppercase">Configuración de Parámetros Globales (Cerebro del Sistema)</h1>
                </div>

                <Link
                    to="/parametrizacion/parametros-globales/nuevo"
                    className="flex items-center gap-1 transition-all rounded-sm text-[11px] font-bold px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm uppercase"
                >
                    <PlusCircle className="w-3.5 h-3.5" />
                    AÑADIR PARÁMETRO
                </Link>
            </div>

            {/* Table Area */}
            <div className="overflow-x-auto">
                {error && (
                    <div className="m-4 p-4 bg-red-50 border border-red-200 text-red-700 flex items-center rounded-sm text-sm">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        {error}
                    </div>
                )}

                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-[#f8fafc] border-b border-gray-200 text-gray-400 text-[10px] font-bold uppercase tracking-wider">
                            <th className="px-6 py-4 border-r border-gray-100">Clave del Parámetro</th>
                            <th className="px-6 py-4 border-r border-gray-100">Descripción</th>
                            <th className="px-6 py-4 border-r border-gray-100 text-center">Valor Numérico</th>
                            <th className="px-6 py-4 border-r border-gray-100">Valor Texto</th>
                            <th className="px-6 py-4 text-center w-48">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm text-gray-600">
                        {loading ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-12 text-center">
                                    <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin mx-auto" />
                                </td>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-12 text-center text-gray-500 italic">
                                    No hay parámetros globales configurados.
                                </td>
                            </tr>
                        ) : (
                            data.map((item) => (
                                <tr key={item.clave} className="border-b border-gray-100 hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4 border-r border-gray-100 font-bold text-slate-700 uppercase font-mono tracking-tight">
                                        {item.clave}
                                    </td>
                                    <td className="px-6 py-4 border-r border-gray-100 text-gray-500 text-xs">
                                        {item.descripcion}
                                    </td>
                                    <td className="px-6 py-4 border-r border-gray-100 text-center font-bold text-indigo-600">
                                        {item.valor !== null ? (
                                            <span className="flex items-center justify-center gap-1">
                                                <Hash className="w-3 h-3 opacity-50" /> {item.valor}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td className="px-6 py-4 border-r border-gray-100 text-gray-700 italic truncate max-w-[200px]">
                                        {item.valorTexto || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex justify-center items-center gap-2">
                                            <Link
                                                to={`/parametrizacion/parametros-globales/${item.clave}/editar`}
                                                className="text-indigo-600 hover:text-indigo-800 font-bold text-[11px] uppercase"
                                            >
                                                EDITAR
                                            </Link>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                onClick={() => handleDelete(item.clave)}
                                                className="text-red-600 hover:text-red-800 font-bold text-[11px] uppercase"
                                            >
                                                ELIMINAR
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Hint Footer */}
            <div className="p-4 bg-slate-50 border-t border-gray-200 text-[11px] text-gray-400 italic">
                * Los parámetros globales controlan el comportamiento central de la aplicación (tasas, límites, etc.). Proceda con precaución.
            </div>
        </div>
    );
}
